import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import MeasuredBy from "../molecules/MeasuredBy";
import { isFootwear } from "../../utils/categoryParams";

import ArrowSelectIcon from "../../icons/ArrowSelectIcon";
import PhotoCameraIcon from "../../assets/icons/PhotoCameraIcon";
import HelpIcon from "../../assets/icons/HelpIcon";

import {
  choiceScreenUI,
  font,
  subtitlesStyles,
  titleStyles,
} from "../../configs";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";
import { findBrandByDomain } from "../../configs/configLoader";

import "./index.css";

interface StepDeviceSelectProps {
  step: {
    number: number;
  } | null;
  nextPhotoStep: () => void;
  nextQuestionsStep: () => void;
}

const StepDeviceSelect: React.FC<StepDeviceSelectProps> = ({
  step,
  nextPhotoStep,
  nextQuestionsStep,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation("components/device_select");

  const [hoveredBlock, setHoveredBlock] = useState<
    "questions" | "photo" | null
  >(null);

  const brandDefined = findBrandByDomain();

  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";


  const desktopStyles = !isMobile
    ? {
        marginTop: isFootwear ? "50px" : "100px",
        gap: "20px",
      }
    : undefined;

  const getButtonStyles = (type: "questions" | "photo") => {
    const defaultDeviceSelect = {
      default: {
        backgroundColor: "#FFFFFF",
        fontColor: "#000000",
        borderRadius: "0px",
        borderColor: "#000000",
        borderWidth: "1px",
        fontSize: "12px",
        fontWeight: "400",
        textTransform: "",
      },
      hover: {
        backgroundColor: "#FFFFFF",
        fontColor: "#000000",
        borderRadius: "0px",
        borderColor: "#000000",
        borderWidth: "2px",
        fontSize: "12px",
        fontWeight: "400",
        textTransform: "",
      },
    };

    const deviceSelect =
      (choiceScreenUI as any)?.deviceSelect || defaultDeviceSelect;

    let state: "default" | "hover" = "default";
    if (hoveredBlock === type) {
      state = "hover";
    }

    return {
      backgroundColor: deviceSelect[state]?.backgroundColor,
      color: deviceSelect[state]?.fontColor,
      borderRadius: deviceSelect[state]?.borderRadius,
      borderColor: deviceSelect[state]?.borderColor,
      borderWidth: deviceSelect[state]?.borderWidth,
      borderStyle: "solid",
      fontSize: deviceSelect[state]?.fontSize,
      fontWeight: deviceSelect[state]?.fontWeight,
      textTransform: deviceSelect[state]?.textTransform,
    };
  };

  const fontFamily = `${font}, sans-serif`;

  const handleBlockClick = (type: "questions" | "photo") => {
    if (type === "questions") nextQuestionsStep();
    else nextPhotoStep();
  };

  const buttonConfigs: {
    type: "questions" | "photo";
    icon: JSX.Element;
    title: string;
    description: string;
  }[] = [
    {
      type: "questions",
      icon: <HelpIcon fill={titleStyles.color} />,
      title: isFootwear ? t("shoe.titleQuestions") : t("titleQuestions"),
      description: isFootwear
        ? t("shoe.descriptionQuestions")
        : t("descriptionQuestions"),
    },
    {
      type: "photo",
      icon: <PhotoCameraIcon fill={titleStyles.color} />,
      title: isFootwear ? t("shoe.titleScan") : t("titleScan"),
      description: isFootwear
        ? t("shoe.descriptionScan")
        : t("descriptionScan"),
    },
  ];

  const isCustomBrands = isIntersport || isBlackstore;

  const orderedButtons =
    isFootwear && !isCustomBrands
      ? [...buttonConfigs].reverse()
      : buttonConfigs;

  const getTitleStyles = (btnType: "questions" | "photo") => ({
    fontFamily,
    fontSize: isMobile
      ? `calc(${getButtonStyles(btnType).fontSize} - 2px)`
      : getButtonStyles(btnType).fontSize,
    whiteSpace: "nowrap",
  });

  const getDescriptionStyles = (btnType: "questions" | "photo") => ({
    fontFamily,
    fontSize: isMobile
      ? `calc(${getButtonStyles(btnType).fontSize} - 3px)`
      : getButtonStyles(btnType).fontSize,
  });

  return (
    <>
      {isFootwear ? (
        <span
          className="description"
          style={{
            marginTop: "10px",
            ...subtitlesStyles,
            justifyContent:
              subtitlesStyles.textAlign as React.CSSProperties["justifyContent"],
            textAlign:
              subtitlesStyles.textAlign as React.CSSProperties["textAlign"],
            textTransform: (subtitlesStyles.textTransform === "capitalize"
              ? "none"
              : subtitlesStyles.textTransform) as React.CSSProperties["textTransform"],
          }}
        >
          {subtitlesStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("shoe.shoesDescription"))
            : t("shoe.shoesDescription")}
        </span>
      ) : null}
      <Box className="main-container" style={desktopStyles}>
        {orderedButtons.map((btn) => (
          <Box
            key={btn.type}
            className="button-container"
            tabIndex={0}
            role="button"
            onClick={() => handleBlockClick(btn.type)}
            style={getButtonStyles(btn.type)}
            onMouseEnter={() => setHoveredBlock(btn.type)}
            onMouseLeave={() => setHoveredBlock(null)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          >
            <Box className="parts-container">
              <Box className="title-container">
                <Box className="icon-text">
                  {btn.icon}
                  <Typography
                    className="title"
                    style={getTitleStyles(btn.type)}
                  >
                    {btn.title}
                  </Typography>
                </Box>
                <Typography
                  className="title2"
                  style={getDescriptionStyles(btn.type)}
                >
                  {btn.description}
                </Typography>
              </Box>
              <ArrowSelectIcon />
            </Box>
          </Box>
        ))}
      </Box>
      <MeasuredBy step={step} />
    </>
  );
};

export default StepDeviceSelect;
